import React from 'react'
import qs from 'qs'
import { debounce } from 'lodash'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import {
  COMPLETED_OPTIONS,
  DUE_RANGE
} from '../../pages/Tasks/Tabs/AllTasksTab/helpers/parse'
import { uniqueArray } from '../../utilities'
import { addView } from '@SonarCX/slices/views'
import { useUIPaginationState } from '../UIPaginationStateProvider'
import { useEnvironment } from '../EnvironmentProvider'

export const INITIAL_VALUE = {
  leadValue: 'null',
  leadID: 'null',

  ownerValue: 'null',
  ownerID: 'null',

  taskValue: 'null',
  taskID: 'null',

  watcherValue: 'null',
  watcherID: 'null',

  categoryValue: 'null',
  statusValue: 'null',
  typeValue: 'null',
  priorityValue: 'null',

  dueValue: 'null',
  createdValue: 'null',

  completeValue: COMPLETED_OPTIONS[0],

  searchFilter: '',

  dueStartValue: 'null',
  dueEndValue: 'null',

  clientValue: 'null',
  clientID: 'null',

  teamValue: 'null'
}

type TaskFiltersContextType = {
  state: typeof INITIAL_VALUE
  setState: React.Dispatch<React.SetStateAction<typeof INITIAL_VALUE>>
  count: number
  clearAll: () => void
  handleChange: (key: string) => (val: any) => void
  updateSearch: (tempSearch: string) => () => void
  expanded: string[]
  toggleExpand: (key: string) => void
  includeSubtasks: boolean
  setIncludeSubtasks: React.Dispatch<React.SetStateAction<boolean>>
  onlyFavorites: boolean
  setOnlyFavorites: React.Dispatch<React.SetStateAction<boolean>>
  saveFilter: (args: {
    name: string
    url: string
    onFinish?: () => void
  }) => void
}

const TaskFiltersContext = React.createContext<TaskFiltersContextType>(null)

const DEBOUNCE_DELAY = 1000

const TaskFiltersProvider = ({ children }) => {
  const dispatch = useAppDispatch()

  const { reset } = useUIPaginationState()
  const { env } = useEnvironment()

  const [state, setState] = React.useState(INITIAL_VALUE)
  const [expanded, setExpanded] = React.useState([])
  const [includeSubtasks, setIncludeSubtasks] = React.useState(false)
  const [onlyFavorites, setOnlyFavorites] = React.useState(false)

  const TASK_FILTERS_KEY = React.useMemo(
    () => `task_filters-${env?.name ?? ''}`,
    [env]
  )
  const count: TaskFiltersContextType['count'] = React.useMemo(() => {
    const {
      searchFilter,
      completeValue,
      taskID,
      dueStartValue,
      dueEndValue,
      ...rest
    } = state
    const notNull = Object.values(rest).filter(val => val !== 'null')
    const notNullCount = notNull.length

    if (completeValue === COMPLETED_OPTIONS[0]) {
      return notNullCount
    }

    return notNullCount + 1
  }, [state])

  const clearAll: TaskFiltersContextType['clearAll'] = React.useCallback(() => {
    setState(old => {
      const { searchFilter } = old
      const newState = { ...INITIAL_VALUE, searchFilter }
      sessionStorage.setItem(TASK_FILTERS_KEY, JSON.stringify(newState))
      return newState
    })
    reset('All Tasks')
  }, [TASK_FILTERS_KEY, reset])

  const handleChange: TaskFiltersContextType['handleChange'] =
    React.useCallback(
      key => val => {
        let newState = { ...state, [key]: val }
        setState(newState)

        if (key === 'clientValue') {
          newState = { ...newState, clientID: INITIAL_VALUE.clientID }
          setState(newState)
        }

        if (key === 'taskValue') {
          newState = { ...newState, taskID: INITIAL_VALUE.taskID }
          setState(newState)
        }

        if (key === 'dueValue' && val !== DUE_RANGE) {
          newState = {
            ...newState,
            dueStartValue: INITIAL_VALUE.dueStartValue,
            dueEndValue: INITIAL_VALUE.dueEndValue
          }
          setState(newState)
        }

        sessionStorage.setItem(TASK_FILTERS_KEY, JSON.stringify(newState))
        reset('All Tasks')
      },
      [TASK_FILTERS_KEY, reset, state]
    )

  const updateSearch: TaskFiltersContextType['updateSearch'] =
    React.useCallback(
      tempSearch => {
        const func = debounce(() => {
          setState(old => {
            return {
              ...old,
              searchFilter: tempSearch
            }
          })
          reset('All Tasks')
        }, DEBOUNCE_DELAY)

        func()

        return func.cancel
      },
      [reset]
    )

  const toggleExpand: TaskFiltersContextType['toggleExpand'] =
    React.useCallback(key => {
      setExpanded(old => {
        if (old.includes(key)) {
          return old.filter(x => x !== key)
        }

        return uniqueArray(old, key)
      })
    }, [])

  const saveFilter: TaskFiltersContextType['saveFilter'] = React.useCallback(
    ({ name, url, onFinish }) => {
      const filter = qs.parse(url)

      // Need to correctly unwrap the array because qs encodes it a certain way
      if (Array.isArray(filter.filter_custom?.[0]?.field_id)) {
        const { field_id, logic, value } = filter.filter_custom[0]
        filter.filter_custom = field_id.map((field_id, i) => ({
          field_id: field_id,
          logic: logic[i],
          value: value[i]
        }))
      }

      const toSave: ViewUpdate<APITaskViewFilter> = {
        name,
        type: 'task',
        item_id: null,
        user_id: null,
        role_id: null,
        filter
      }

      dispatch(
        addView(toSave, false, () => {
          onFinish && onFinish()
        })
      )
    },
    [dispatch]
  )

  React.useEffect(() => {
    const filters = sessionStorage.getItem(TASK_FILTERS_KEY)
    if (filters) {
      setState(JSON.parse(filters))
    }
  }, [TASK_FILTERS_KEY])

  return (
    <TaskFiltersContext.Provider
      value={{
        state,
        setState,
        count,
        clearAll,
        handleChange,
        updateSearch,
        expanded,
        toggleExpand,
        includeSubtasks,
        setIncludeSubtasks,
        onlyFavorites,
        setOnlyFavorites,
        saveFilter
      }}
    >
      {children}
    </TaskFiltersContext.Provider>
  )
}

export default TaskFiltersProvider

export const useTaskFilters = () => React.useContext(TaskFiltersContext)
